import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { PublicAuditResults } from "@/components/audit/PublicAuditResults";
import { getPublicAuditRun } from "@/lib/audit.functions";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";

function publicAuditQueryOptions(slug: string, locale: "en" | "es") {
  return queryOptions({
    queryKey: ["publicAudit", locale, slug],
    queryFn: () => getPublicAuditRun({ data: { slug, locale } }),
  });
}

async function loadBoth(slug: string) {
  const [en, es] = await Promise.all([
    getPublicAuditRun({ data: { slug, locale: "en" } }),
    getPublicAuditRun({ data: { slug, locale: "es" } }),
  ]);
  return { en, es };
}

export const Route = createFileRoute("/audit/results/$slug")({
  loader: async ({ params, context }) => {
    const both = await loadBoth(params.slug);
    if (!both.en && !both.es) throw notFound();
    if (!both.en) throw notFound();
    await context.queryClient.ensureQueryData(publicAuditQueryOptions(params.slug, "en"));
    return { siblingAvailable: Boolean(both.es) };
  },
  head: ({ params, loaderData }) => {
    const slug = params.slug;
    const hostDisplay = slug.replace(/-/g, ".");
    const title = `${hostDisplay}: Free Shopify Store Audit · Shugert`;
    const description = `See the free Shopify store audit for ${hostDisplay}: performance, SEO, CRO, and AEO (AI readability) scores from Shugert.`;
    const path = `/audit/results/${slug}`;
    return buildHead({
      noindex: true,
      locale: "en",
      title,
      description,
      path,
      // Only point hreflang at the Spanish sibling report when one genuinely
      // exists. Falling back to the generic /es/auditoria page created a
      // one-way hreflang link to a page that never links back (that page
      // isn't a translation of this specific domain's report), which is
      // exactly the "missing reciprocal hreflang" pattern crawlers flag.
      // Self-referencing suppresses the pair entirely via buildHead's
      // enHref !== esHref check, same as any other English-only page.
      altPath: loaderData?.siblingAvailable ? `/es/auditoria/resultados/${slug}` : path,
      jsonLd: breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Free Store Audit", path: "/audit" },
        { name: hostDisplay, path },
      ]),
    });
  },
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold">Something went wrong</h1>
          <p className="mt-3 text-muted-foreground">We couldn't load this audit right now.</p>
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="mt-6 inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground/40"
          >
            Try again
          </button>
        </div>
      </SiteShell>
    );
  },
  notFoundComponent: () => {
    const params = Route.useParams();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold">No audit found for this store</h1>
          <p className="mt-3 text-muted-foreground">
            We don't have a public audit for{" "}
            <span className="font-mono">{params.slug.replace(/-/g, ".")}</span> yet.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="/audit"
              className="inline-flex rounded-lg bg-foreground px-4 py-2 text-sm font-semibold text-background hover:opacity-90"
            >
              Run a free audit
            </a>
            <a
              href={`/es/auditoria/resultados/${params.slug}`}
              className="inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground/40"
            >
              Ver en español
            </a>
          </div>
        </div>
      </SiteShell>
    );
  },
  component: function AuditResultsPage() {
    const params = Route.useParams();
    const { siblingAvailable } = Route.useLoaderData();
    const { data } = useSuspenseQuery(publicAuditQueryOptions(params.slug, "en"));
    if (!data) throw notFound();
    return (
      <SiteShell>
        <PublicAuditResults run={data} locale="en" siblingLocaleAvailable={siblingAvailable} />
      </SiteShell>
    );
  },
});
